export type Locale = "nl" | "en"

export const LOCALES: Locale[] = ["nl", "en"]

/** Every string value that exists in both site languages. */
export type Localized<T> = Record<Locale, T>

/**
 * Base shape every page in the registry derives from.
 * Titles/descriptions feed the metadata system, slugs feed routing,
 * sitemap generation and prerendering.
 */
export interface SeoPage {
  /** Stable identifier used for internal linking, hreflang pairing and redirects. */
  id: string
  slug: Partial<Record<Locale, string>>
  title: Localized<string>
  description: Localized<string>
  h1?: Localized<string>
  indexable?: boolean
  lastModified?: string
}

export type PageType =
  | "home"
  | "about"
  | "contact"
  | "privacy"
  | "service-index"
  | "service"
  | "location"
  | "case-index"
  | "case-study"
  | "knowledge-index"
  | "knowledge-article"

export type PageId =
  | "home"
  | "about"
  | "contact"
  | "privacy"
  | "services"
  | `service:${string}`
  | `location:${string}`
  | "cases"
  | `case:${string}`
  | "insights"
  | `insight:${string}`

export interface ServicePage extends SeoPage {
  pageType: Extract<PageType, "service">
  shortDescription: Localized<string>
  intro: { heading: Localized<string>; body: Localized<string> }
  capabilities: { title: Localized<string>; description: Localized<string> }[]
  suitableFor: Localized<string>[]
  process: { title: Localized<string>; description: Localized<string> }[]
  faq?: { question: Localized<string>; answer: Localized<string> }[]
  relatedCaseIds: string[]
  relatedKnowledgeIds: string[]
}

export interface LocationPage extends SeoPage {
  pageType: Extract<PageType, "location">
  cityOrRegion: string
  intro: Localized<string>
  localContext: { title: Localized<string>; body: Localized<string> }[]
  localProofCaseIds: string[]
  relevantServiceIds: string[]
}

export interface CaseStudy extends SeoPage {
  pageType: Extract<PageType, "case-study">
  clientName: string
  location?: Localized<string>
  industry: Localized<string>
  summary: Localized<string>
  problem: Localized<string>
  solution: Localized<string>
  outcome: Localized<string>
  image: string
  gradientColor?: string
  relatedServiceIds: string[]
  relatedKnowledgeIds?: string[]
}

export type KnowledgeSection =
  | { type: "heading"; text: string }
  | { type: "paragraph"; text: string }
  | { type: "list"; items: string[] }
  | { type: "callout"; title?: string; text: string }

export interface KnowledgeArticle extends SeoPage {
  pageType: Extract<PageType, "knowledge-article">
  published: string
  modified: string
  author: string
  sections: KnowledgeSection[]
  relatedServiceIds: string[]
  relatedCaseIds: string[]
}
